import React, { useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import { all } from 'axios';
import { useSession } from '@cordata/buffet';
import kebabcaseKeys from 'kebabcase-keys';
import camelcaseKeys from 'camelcase-keys';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  capitalize,
  pickBy,
  orderBy,
  kebabCase,
  values,
} from 'lodash';

import refGeoActions from '~/utils/geo/reference_geo';
import { deburrObject } from '~/utils/api/normalizers';
import {
  authorizedPyInstance as appUserInstance,
  citInstance as agencyUserInstance,
} from '~/utils/api/instances';

import consts from './const';

const CitContext = createContext({});

const buildPayload = (v) => {
  const p = kebabcaseKeys(Object.keys(v).reduce((a, m) => {
    let value;

    if (m.includes('Uid') && v[m] && v[m].id) {
      value = Array.isArray(v[m]) ? v[m].map(vvv => vvv.id) : v[m].id;
    } else if (Array.isArray(v[m])) {
      value = v[m].map(vv => vv.value);
    } else if (m === 'phoneType') {
      value = capitalize(v[m]);
    } else if (typeof (v[m]) === 'object' && v[m]?.value) {
      /* eslint-disable-next-line */
              value = v[m].value;
    } else {
      value = v[m] !== '' ? v[m] : undefined;
    }

    return {
      ...a,
      [m]: value,
    };
  }, {
  }), {
    exclude: ['addr1', 'addr2'],
  });

  const referralSource = JSON.parse(v.referralSourceUid);
  p['referral-source-uid'] = referralSource.id;
  p['referral-source-label'] = referralSource.referralSourceLabel;
  p['location-addr1'] = v.locationAddr1;
  p['location-addr2'] = v.locationAddr2;

  p['facility-uid'] = Array.isArray(v.facilityUid) ? v.facilityUid?.map(vv => vv.id) : v.facilityUid?.id;
  p['gender-label'] = v.genderUid?.genderLabel;
  p['race-uid'] = v.raceUid?.map(vv => vv.id);
  p['shift-label'] = v.shiftUid?.shiftLabel;
  p['state-label'] = v.stateUid?.divisionLabel;
  p['location-state-label'] = v.locationStateUid?.divisionLabel;
  p['disposition-label'] = v.dispositionUid?.statusLabel.split(' - ').pop();

  delete p.code;
  delete p.officerName;
  delete p.facilityUid;
  delete p.genderUid;
  delete p.raceUid;
  delete p.shiftUid;
  delete p.stateUid;
  delete p.locationStateUid;
  delete p['approval-status'];

  if (v.patient) { // if a duplicate patient was selected
    p['patient-uid'] = v.patient.summaryPatient.patientUid;
    delete p.patient;
  }

  return p;
};

const buildActions = contextType => {
  let instance;
  let namespace = '';
  let refNamespace = 'reference';
  let actions = {};

  switch (contextType) {
    case 'appUser':
      instance = appUserInstance;
      namespace = 'api/data';
      refNamespace = 'reference';
      actions = {
        submit: v =>
          instance.put(`${namespace}/public/cit-submission/${v.id}`, buildPayload(v)),
        approveSubmission: v =>
          instance.put(`${namespace}/public/cit-submission/${v.id}/accept`, buildPayload(v)),
        rejectSubmission: v =>
          instance.put(`${namespace}/public/cit-submission/${v.id}/reject`, {}),
        successRedirect: history => {
          history.push('/home');
        },
      };
      break;
    case 'agencyUser':
      instance = agencyUserInstance;
      refNamespace = 'config';
      actions = {
        getContext: v =>
          instance.get('context', v),
        identify: v =>
          instance.post('identify', kebabcaseKeys(v)),
        submit: v =>
          instance.post('submit', buildPayload(v)),
      };
      break;
    default:
      break;
  }

  return {
    getPublic: ({
      resourceName, resourcePrefix, resourceSuffix, ...p
    }) =>
      instance.get(`${namespace}/public/${resourcePrefix}${resourceName}${resourceSuffix}`, p),
    getConfig: ({ resourceName, ...p }) =>
      instance.get(`${namespace}/${refNamespace}/${resourceName}`, p),
    mapDataToFormData: (data, fd) => {
      let pickedRaces;
      if (data.raceUid) {
        pickedRaces = Array.isArray(data.raceUid)
          ? fd?.races?.filter(i => data.raceUid.includes(i.id))
          : fd?.races?.filter(i => i.id === data.raceUid);
      }

      return pickBy({
        stateUid: fd.states?.find(i => i.id === data.stateUid),
        locationStateUid: fd.states?.find(i => i.id === data.locationStateUid),
        genderUid: fd.genders?.find(i => i.id === data.genderUid),
        raceUid: pickedRaces,
        shiftUid: data.shiftUid,
        dispositionUid: fd.dispositions?.find(i => i.id === data.dispositionUid),
        referralSourceUid: JSON.stringify(fd.referralSourceOptions?.find(i =>
          i.id === data.referralSourceUid)),
        facilityUid: Array.isArray(data.facilityUid)
          ? fd?.facilities?.filter(i => data.facilityUid.includes(i.id))
          : data.facilityUid
            ? [fd?.facilities?.find(i => i.id === data.facilityUid)].filter(Boolean)
            : [],
        forceUsedIncidentResultedInInjury:
          fd?.forceUsedIncidentResultedInInjuryOptions
            ?.filter(i => data.forceUsedIncidentResultedInInjury?.includes(i.label)),
        weaponInvolved:
          fd?.weaponInvolvedOptions
            ?.filter(i => data.weaponInvolved?.includes(i.label)),
        incidentHasRelatedCrimeYes:
          fd?.incidentHasRelatedCrimeOptions
            ?.filter(i => data.incidentHasRelatedCrimeYes?.includes(i.label)),
      }, (v) => v);
    },
    patientDuplicateCheck: ({
      firstName,
      lastName,
      genderLabel,
      birthDate,
    }) => new Promise((resolve, reject) => {
      instance
        .get(`${namespace}/public/person-match`, {
          params: {
            'first-name': firstName,
            'last-name': lastName,
            'gender-label': genderLabel,
            ...birthDate && { 'birth-date': format(birthDate, 'MM-DD-YYYY') },
          },
        })
        .then(r => r.data.data.map(deburrObject))
        .then(r => resolve(r))
        .catch(e => reject(e));
    }),
    getPreviousSubmissions: personUid =>
      instance
        .get(`${namespace}/public/person/${personUid}/incidents`)
        .then(r => r.data.data.map(deburrObject))
        .then((results) => {
          return results.reduce((submissions, result) => {
            let parsedSubmission = {};

            try {
              parsedSubmission = camelcaseKeys(JSON.parse(result.value));

              submissions.push({
                id: result.id,
                ...parsedSubmission,
              });
            } catch {
              /* eslint-disable-next-line */
              console.error('Malformed previous submission:', result);
            }

            return submissions;
          }, []);
        })
        .then(r => orderBy(r, ['dateTime'], ['desc'])),
    postalAutoComplete: async (zip) => {
      const countyPromise = refGeoActions.fetch(zip, instance, `${namespace}/${refNamespace}`);

      let county = '';
      let state = '';
      let city = '';

      try {
        const refGeo = await countyPromise;
        ({ county, state, city } = refGeo);
      } catch {
        county = '';
      }

      state = await refGeoActions.divisionName(state, instance, `${namespace}/${refNamespace}`);

      return {
        city,
        zip,
        state,
        county,
      };
    },
    ...actions,
  };
};

const CitProvider = ({ children }) => {
  const { isAuthed } = useSession();
  const [authStatus, setAuthStatus] = useState('CHECKING');
  const [genders, setGenders] = useState([]);
  const [dispositions, setDispositions] = useState([]);
  const [states, setStates] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [races, setRaces] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [referralSourceOptions, setReferralSourceOptions] = useState([]);
  const [formDataStatus, setFormDataStatus] = useState('FETCHING');
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [previousData, setPreviousData] = useState();
  const [, i18n] = useTranslation();

  const userType = isAuthed ? 'appUser' : 'agencyUser';
  const actions = buildActions(userType);

  const formDataFetches = [{
    type: 'gender',
    getter: actions.getConfig,
    setter: setGenders,
  }, {
    type: 'referral-source',
    getter: actions.getConfig,
    setter: setReferralSourceOptions,
  }, {
    type: 'race',
    getter: actions.getConfig,
    setter: setRaces,
  }, {
    type: 'shift',
    getter: actions.getConfig,
    setter: setShifts,
  }, {
    type: 'country-division',
    getter: actions.getConfig,
    setter: setStates,
    'country-abbrev-iso': 'US',
  }, {
    type: 'status',
    getter: actions.getConfig,
    setter: setDispositions,
    ref_data_type: 'Episode',
  }, {
    type: 'facility',
    resourceSuffix: '-with-distribution',
    getter: actions.getPublic,
    setter: setFacilities,
    'is-in-selections': true,
  }];

  const buildFetch = ({
    type, getter, resourcePrefix, resourceSuffix, ...params
  }) =>
    getter({
      resourceName: getter.name === 'getConfig' ? `reference-${type}` : type,
      resourcePrefix: getter.name === 'getConfig' ? 'reference-' : (resourcePrefix || ''),
      resourceSuffix: userType === 'appUser' ? (resourceSuffix || '') : '',
      params: {
        ...params,
        sort: `default-sort-order,${type}-name,${type}-label`,
        'all-data': 1,
      },
    });

  const fetchAll = () => {
    all(formDataFetches.map(i => buildFetch(i)))
      .then(r => {
        formDataFetches.forEach((fd, idx) => {
          fd.setter(r[idx].data.data.map(deburrObject));
        });
        setFormDataStatus('SUCCESS');
      })
      .catch(e => {
        /* eslint-disable-next-line */
        console.log(e);
        setFormDataStatus('ERROR');
      });
  };

  useEffect(() => {
    setFormDataStatus('FETCHING');
  }, []);

  const resetFormData = () => {
    setGenders([]);
    setRaces([]);
    setShifts([]);
    setStates([]);
    setDispositions([]);
    setFacilities([]);
    setReferralSourceOptions([]);
  };

  const fetchData = () => {
    resetFormData();
    fetchAll();
  };

  const getContext = v => {
    if (isAuthed) {
      fetchData();
    } else {
      actions.getContext(v)
        .then((r) => {
          if (r.data.data.translations) {
            const [l, d] = kebabCase(Object.keys(r.data.data.translations)).split('-');

            if (l && d) {
              i18n.addResourceBundle('en', 'US', values(r.data.data.translations)[0]);
            }
          }

          fetchData();
          setAuthStatus('VALID');
        })
        .catch(() => {
          setAuthStatus('INVALID');
        });
    }
  };

  useEffect(getContext, []);

  return (
    <CitContext.Provider
      value={{
        ...consts,
        authStatus,
        formDataStatus,
        previousData,
        isDataLoaded,

        actions: {
          ...actions,

          getContext,
          setAuthStatus,
          setPreviousData,
          setIsDataLoaded,
        },

        formData: {
          ...consts.formData,
          states,
          referralSourceOptions,
          races,
          shifts,
          genders,
          dispositions,
          facilities,
        },
      }}
    >
      {children}
    </CitContext.Provider>
  );
};

CitProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {
  CitContext,
  CitProvider,
};
